import { forwardRef } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronsDownUp, ChevronsUpDown, LayoutGrid, List, Search, Star, X, type LucideIcon } from "lucide-react";
import {
  getFavoriteChipStyle,
  getSourceChipStyle,
  type SourceTokenKey,
} from "@/lib/readwiseSource";


type SourceOption = { key: string; label: string; Icon: LucideIcon; tokenKey: SourceTokenKey };

export type Density = "cards" | "compact";

type Props = {
  search: string;
  onSearchChange: (v: string) => void;
  bookFilter: string;
  onBookFilterChange: (v: string) => void;
  tagFilter: string;
  onTagFilterChange: (v: string) => void;
  sourceFilter: string;
  onSourceFilterChange: (v: string) => void;
  books: string[];
  favoriteTag: string | null;
  favoriteCount: number;
  otherTags: string[];
  sources: SourceOption[];
  density: Density;
  onDensityChange: (d: Density) => void;
  showExpandControls?: boolean;
  allExpandedOnPage?: boolean;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
};

export const HighlightFilters = forwardRef<HTMLInputElement, Props>(({
  search,
  onSearchChange,
  bookFilter,
  onBookFilterChange,
  tagFilter,
  onTagFilterChange,
  sourceFilter,
  onSourceFilterChange,
  books,
  favoriteTag,
  favoriteCount,
  otherTags,
  sources,
  density,
  onDensityChange,
  showExpandControls = false,
  allExpandedOnPage = false,
  onExpandAll,
  onCollapseAll,
}, searchInputRef) => {
  const hasSearch = search.trim().length > 0;
  const hasBook = bookFilter !== "__all__";
  const hasTag = tagFilter !== "__all__";
  const hasSource = sourceFilter !== "__all__";
  const activeCount = [hasSearch, hasBook, hasTag, hasSource].filter(Boolean).length;

  const activeSource = hasSource ? sources.find((s) => s.key === sourceFilter) : null;
  const isFavoriteActive =
    hasTag && favoriteTag && tagFilter.toLowerCase() === favoriteTag.toLowerCase();

  const clearAll = () => {
    onSearchChange("");
    onBookFilterChange("__all__");
    onTagFilterChange("__all__");
    onSourceFilterChange("__all__");
  };

  return (
    <div className="space-y-3">
      <div className="hidden md:flex justify-end items-center gap-2">
        {showExpandControls && (
          <div className="inline-flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  aria-label={allExpandedOnPage ? "Collapse all on this page" : "Expand all on this page"}
                  onClick={() => (allExpandedOnPage ? onCollapseAll?.() : onExpandAll?.())}
                >
                  {allExpandedOnPage ? (
                    <ChevronsDownUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronsUpDown className="h-3.5 w-3.5" />
                  )}
                  <span className="text-xs">{allExpandedOnPage ? "Collapse all" : "Expand all"}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {allExpandedOnPage ? "Collapse all rows on this page" : "Expand all rows on this page"}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        <div className="inline-flex items-center rounded-md border bg-background p-0.5">
          <Button
            type="button"
            variant={density === "cards" ? "secondary" : "ghost"}
            size="sm"
            className="h-7 px-2"
            aria-label="Card view"
            aria-pressed={density === "cards"}
            onClick={() => onDensityChange("cards")}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span className="text-xs">Cards</span>
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={density === "compact" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 px-2"
                aria-label="Compact view"
                aria-pressed={density === "compact"}
                onClick={() => onDensityChange("compact")}
              >
                <List className="h-3.5 w-3.5" />
                <span className="text-xs">Compact</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>Compact view · J/K or ↑/↓ to navigate</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <Input
          ref={searchInputRef}
          placeholder="Search highlights…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onSearchChange("");
              e.currentTarget.blur();
            }
          }}
          data-search-input
        />
        <Select value={bookFilter} onValueChange={onBookFilterChange}>
          <SelectTrigger>
            <SelectValue placeholder="All books" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All books</SelectItem>
            {books.map((b) => (
              <SelectItem key={b} value={b}>
                {b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tagFilter} onValueChange={onTagFilterChange}>
          <SelectTrigger>
            <SelectValue placeholder="All tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All tags</SelectItem>
            {favoriteTag && (
              <>
                <SelectItem value={favoriteTag}>
                  <span
                    className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border text-xs"
                    style={getFavoriteChipStyle()}
                  >
                    <Star className="h-3.5 w-3.5 fill-current" />
                    Favorite
                    <span className="opacity-70 ml-0.5">({favoriteCount})</span>
                  </span>
                </SelectItem>
                <SelectSeparator />
              </>
            )}
            {otherTags.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={onSourceFilterChange}>
          <SelectTrigger>
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All sources</SelectItem>
            {sources.map(({ key, label, Icon, tokenKey }) => (
              <SelectItem key={key} value={key}>
                <span
                  className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded border text-xs"
                  style={getSourceChipStyle(tokenKey)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {activeCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filtered by:</span>

          {hasSearch && (
            <Badge variant="outline" className="border-muted-foreground/40 text-foreground bg-muted gap-1.5">
              <Search className="h-3 w-3" />
              {search}
              <button
                onClick={() => onSearchChange("")}
                aria-label={`Remove search filter ${search}`}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5 -mr-1"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {hasBook && (
            <Badge variant="outline" className="border-primary text-primary bg-primary/10 gap-1.5">
              {bookFilter}
              <button
                onClick={() => onBookFilterChange("__all__")}
                aria-label={`Remove book filter ${bookFilter}`}
                className="hover:bg-primary/20 rounded-full p-0.5 -mr-1"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {hasTag &&
            (isFavoriteActive ? (
              <Badge
                variant="outline"
                className="gap-1.5 border"
                style={getFavoriteChipStyle()}
              >
                <Star className="h-3 w-3 fill-current" />
                Favorite
                <button
                  onClick={() => onTagFilterChange("__all__")}
                  aria-label="Remove favorite filter"
                  className="hover:bg-foreground/10 rounded-full p-0.5 -mr-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ) : (
              <Badge variant="outline" className="border-sage text-sage bg-sage/10 gap-1.5">
                {tagFilter}
                <button
                  onClick={() => onTagFilterChange("__all__")}
                  aria-label={`Remove tag filter ${tagFilter}`}
                  className="hover:bg-sage/20 rounded-full p-0.5 -mr-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}

          {hasSource && activeSource && (
            <Badge
              variant="outline"
              className="gap-1.5 border"
              style={getSourceChipStyle(activeSource.tokenKey)}
            >
              <activeSource.Icon className="h-3 w-3" />
              {activeSource.label}
              <button
                onClick={() => onSourceFilterChange("__all__")}
                aria-label={`Remove source filter ${activeSource.label}`}
                className="hover:bg-foreground/10 rounded-full p-0.5 -mr-1"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {activeCount > 1 && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 px-2 text-xs">
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  );
});
HighlightFilters.displayName = "HighlightFilters";
